import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Chip, Heading, SpinningLoader, Text } from '@weave/design-system';
import {
  SchedulingFeatureReadiness,
  useSchedulingFeaturesReady,
} from './use-scheduling-features-ready.hook';

const cardStyle = css`
  border: 1px solid ${theme.colors.gray300};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(2)};
`;

const cardHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1)};

  h3 {
    margin: 0;
  }
`;

const descriptionListStyle = css`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: ${theme.spacing(3)};
  row-gap: ${theme.spacing(1)};
  margin: 0;

  dt,
  dd {
    margin: 0;
  }

  dd {
    word-break: break-word;
  }
`;

const EMPTY = '—';

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : EMPTY;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === undefined || value === null || value === '') {
    return EMPTY;
  }
  return String(value);
};

const formatDate = (value: string) => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? formatValue(value) : `${date.toLocaleString()} (${value})`;
};

const DescriptionList = ({ items }: { items: [string, React.ReactNode][] }) => (
  <dl css={descriptionListStyle}>
    {items.map(([label, value]) => (
      <React.Fragment key={label}>
        <Text as="dt" weight="bold">
          {label}
        </Text>
        <Text as="dd">{value}</Text>
      </React.Fragment>
    ))}
  </dl>
);

const FeatureCard = ({ feature }: { feature: SchedulingFeatureReadiness }) => (
  <div css={cardStyle} data-testid={`scheduling-feature-${feature.name}`}>
    <div css={cardHeaderStyle}>
      <Heading level={3}>{feature.name}</Heading>
      <Chip variant={feature.enabled ? 'success' : 'warn'}>
        {feature.enabled ? 'Enabled' : 'Not Enabled'}
      </Chip>
    </div>
    <DescriptionList
      items={[
        ['Enabled', formatValue(feature.enabled)],
        ['Active', formatValue(feature.active)],
        ['Classification', formatValue(feature.classification)],
        ['Summary', formatValue(feature.summary)],
        ['Reason Codes', formatValue(feature.reasonCodes)],
        ['Recommendation Code', formatValue(feature.recommendationCode)],
        ['Message', formatValue(feature.message)],
        ['Evaluated At', formatDate(feature.evaluatedAt)],
      ]}
    />
  </div>
);

export const EnabledSchedulingFeatures = () => {
  const { data, isLoading, isError, error, orgId, locationGroupId } =
    useSchedulingFeaturesReady();

  if (!orgId || !locationGroupId) {
    return (
      <Text color="light">
        Unable to determine the organization or location group for this location.
      </Text>
    );
  }

  if (isLoading) {
    return <SpinningLoader />;
  }

  if (isError) {
    return (
      <Text color="error">
        Failed to load scheduling feature readiness
        {error instanceof Error ? `: ${error.message}` : '.'}
      </Text>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <div css={cardStyle}>
        <DescriptionList
          items={[
            ['Org ID', formatValue(data.orgId)],
            ['Scope', formatValue(data.scope)],
            ['Parent Location ID', formatValue(data.parentLocationId)],
          ]}
        />
      </div>
      {data.features?.length ? (
        data.features.map((feature) => <FeatureCard key={feature.name} feature={feature} />)
      ) : (
        <Text color="light">No scheduling features returned.</Text>
      )}
    </>
  );
};
